/**
 * DealStatusBadge - Badge visual para status do Deal
 * Exibe cores e ícones diferenciados por status
 * Versão: 1.0.0
 */
import React from 'react';
import { Trophy, XCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { DealStatus } from '@/hooks/useCrmDeals';

// Props do componente
interface DealStatusBadgeProps {
  status: DealStatus;
  className?: string;
  showIcon?: boolean;
}

// Configuração visual por status
const statusConfig: Record<DealStatus, { 
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  className: string;
}> = {
  open: {
    icon: Clock,
    label: 'Aberto',
    className: 'bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/50 dark:text-blue-300'
  },
  won: {
    icon: Trophy,
    label: 'Ganho',
    className: 'bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300'
  },
  lost: {
    icon: XCircle,
    label: 'Perdido',
    className: 'bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/50 dark:text-red-300'
  }
};

/**
 * Badge visual para exibir status do Deal
 */
export function DealStatusBadge({ 
  status, 
  className,
  showIcon = true 
}: DealStatusBadgeProps) {
  const config = statusConfig[status];
  // Defesa em profundidade: se status vier inválido/nulo, não renderiza nada
  if (!config) return null;
  const Icon = config.icon;

  return (
    <Badge 
      variant="secondary"
      className={cn(
        config.className, 
        "text-[10px] px-1.5 py-0 h-5 whitespace-nowrap", // Tamanho reduzido em 30% + previne quebra de linha
        className
      )}
    >
      {showIcon && <Icon className="h-2.5 w-2.5 mr-0.5" />}
      {config.label}
    </Badge>
  );
}
